/**
 * Browser-side `show_in_viewer`: read-only, auto-run. Points at an
 * existing viewer control from the fixed catalogue and guides to it: the
 * control itself if on screen; else its menu button or its workflow step
 * button, with the outline moving on as the clinician opens them; if none
 * is on screen yet, the result says what is needed first and the control
 * is outlined as soon as it appears (watched for 2 minutes). Never clicks
 * or changes anything. Ports injected (React-free).
 */
import { asRecord } from '../core';
import type { ShowInViewerOutput } from '../../contract';
import { SHOW_IN_VIEWER_CATALOGUE, type TargetSpec } from './targets';
import type { ShowInViewerTarget } from '@agent/tool-names';

export type ProbeResult = 'visible' | 'disabled' | 'absent';

export interface ShowInViewerDeps {
    /** Is a control matching this selector on screen (and enabled)? */
    probe(selector: string): ProbeResult;
    /** Guide along the chain (step, menu, control): outline what is on
     *  screen now and follow as the rest appears. */
    highlight(chain: ReadonlyArray<string>): void;
}

const isTarget = (t: unknown): t is ShowInViewerTarget =>
    typeof t === 'string' && Object.hasOwn(SHOW_IN_VIEWER_CATALOGUE, t);

export function showInViewer(
    input: unknown,
    deps: ShowInViewerDeps
): ShowInViewerOutput {
    const target = asRecord(input)?.target;
    if (!isTarget(target)) {
        // The server schema allows only catalogue targets; defensive.
        throw new Error(`unknown show_in_viewer target: ${String(target)}`);
    }
    const spec = SHOW_IN_VIEWER_CATALOGUE[target];
    const base = { target, label: spec.label, howTo: spec.howTo };

    const opener = spec.opener
        ? SHOW_IN_VIEWER_CATALOGUE[spec.opener]
        : undefined;
    const stepSpec = spec.step
        ? SHOW_IN_VIEWER_CATALOGUE[spec.step]
        : undefined;
    // Where the clinician has to go: step button → menu button → control.
    const chain = [stepSpec, opener, spec]
        .filter((s): s is TargetSpec => !!s)
        .map((s) => s.selector);
    // Always guide: even if nothing is on screen yet, the outline appears
    // as soon as the step, menu or control does.
    deps.highlight(chain);

    const state = deps.probe(spec.selector);
    if (state !== 'absent') {
        return {
            status: 'highlighted',
            ...base,
            disabled: state === 'disabled',
            ...(state === 'disabled' ? { needs: spec.needs } : {}),
        };
    }
    for (const first of [opener, stepSpec]) {
        if (first && deps.probe(first.selector) !== 'absent') {
            return {
                status: 'highlighted-opener',
                ...base,
                opener: first.label,
            };
        }
    }
    return { status: 'not-visible', ...base, needs: spec.needs };
}
